import { useCallback, useEffect, useRef, useState } from 'react';
import {
  backoffMs,
  enqueue,
  PendingAnswer,
  settle,
  SaveStatus,
  statusOf,
  toBatch,
} from './autosaveQueue';

const FLUSH_INTERVAL_MS = 1200;

/**
 * Sends a student's answers to the server without ever making them wait.
 *
 * An answer is on screen the moment it is chosen. This queues it, sends the
 * queue as one call, and retries with a growing wait if the server cannot be
 * reached. Nothing here blocks the paper: a student on a bad connection carries
 * on answering and the queue catches up.
 */
export const useAutosave = (
  attemptId: string | undefined,
  save: (batch: ReturnType<typeof toBatch>) => Promise<any>,
  onSaved?: (response: any) => void,
) => {
  const queueRef = useRef<PendingAnswer[]>([]);
  const inFlightRef = useRef(false);
  const failuresRef = useRef(0);
  const nextAttemptRef = useRef(0);
  const stoppedRef = useRef(false);

  // The exam re-renders every second for its clock, and callers pass fresh
  // callbacks each time. Reading them through refs keeps `flush` stable, so the
  // flush timer below is not torn down and restarted before it can ever fire.
  const attemptIdRef = useRef(attemptId);
  const saveRef = useRef(save);
  const onSavedRef = useRef(onSaved);
  attemptIdRef.current = attemptId;
  saveRef.current = save;
  onSavedRef.current = onSaved;

  const [status, setStatus] = useState<SaveStatus>('saved');
  const [pendingCount, setPendingCount] = useState(0);

  const publish = useCallback(() => {
    setPendingCount(queueRef.current.length);
    setStatus(statusOf(queueRef.current, inFlightRef.current, failuresRef.current));
  }, []);

  const flush = useCallback(async (): Promise<void> => {
    if (
      stoppedRef.current ||
      inFlightRef.current ||
      queueRef.current.length === 0 ||
      !attemptIdRef.current
    ) {
      return;
    }
    if (Date.now() < nextAttemptRef.current) {
      return;
    }

    const sending = queueRef.current;
    inFlightRef.current = true;
    publish();

    try {
      const response = await saveRef.current(toBatch(sending));

      // Anything the student changed while this was in flight stays queued.
      queueRef.current = settle(queueRef.current, sending);
      failuresRef.current = 0;
      nextAttemptRef.current = 0;
      onSavedRef.current?.(response);
    } catch (e: any) {
      const status = e?.status;

      // The paper is closed. Retrying would never succeed, and the screen has
      // to be told rather than left spinning.
      if (status === 409 || status === 404) {
        stoppedRef.current = true;
      } else {
        failuresRef.current += 1;
        nextAttemptRef.current = Date.now() + backoffMs(failuresRef.current);
      }
    } finally {
      inFlightRef.current = false;
      publish();
    }
  }, [publish]);

  /** Records a change and lets the queue carry it. */
  const queueAnswer = useCallback(
    (questionId: string, selectedAnswer: string[]) => {
      queueRef.current = enqueue(queueRef.current, {
        questionId,
        selectedAnswer,
        answeredAt: new Date().toISOString(),
      });
      publish();
    },
    [publish],
  );

  useEffect(() => {
    const timer = window.setInterval(() => {
      flush();
    }, FLUSH_INTERVAL_MS);

    // A student coming back to the tab should not wait for the next tick.
    const onWake = () => flush();
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);

    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
    };
  }, [flush]);

  // Closing the tab with answers still queued loses them, so say so first.
  useEffect(() => {
    const onUnload = (event: BeforeUnloadEvent) => {
      if (queueRef.current.length > 0) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, []);

  return { queueAnswer, flush, status, pendingCount, stopped: stoppedRef.current };
};

export default useAutosave;
